import { supabase } from '@/integrations/supabase/client';
import { MISSING_MODULE_QUIZZES } from '@/data/quizContent/quizRegistry';
import { logger } from '@/utils/logger';

interface ModuleMapping {
  moduleId: string;
  courseId: string;
}

const MODULE_COURSE_MAPPINGS: ModuleMapping[] = [
  { moduleId: 'js-basics', courseId: 'js-fundamentals' },
  { moduleId: 'js-functions', courseId: 'js-fundamentals' },
  { moduleId: 'react-components', courseId: 'basic-react' },
  { moduleId: 'k8s-cluster-architecture', courseId: 'advanced-kubernetes' },
  { moduleId: 'k8s-networking', courseId: 'advanced-kubernetes' },
  { moduleId: 'vercel-edge-functions', courseId: 'edge-computing-fundamentals' },
  { moduleId: 'cloudflare-workers', courseId: 'edge-computing-fundamentals' },
  { moduleId: 'unity-fundamentals', courseId: 'game-development-unity' },
  { moduleId: 'unity-scripting', courseId: 'game-development-unity' },
  { moduleId: 'smart-iot-systems', courseId: 'iot-fundamentals' },
  { moduleId: 'iot-security', courseId: 'iot-fundamentals' },
  { moduleId: 'performance-monitoring', courseId: 'performance-testing' },
  { moduleId: 'load-testing', courseId: 'performance-testing' },
  { moduleId: 'rpa-enterprise-architecture', courseId: 'rpa-fundamentals' },
  { moduleId: 'robotics-rl-basics', courseId: 'reinforcement-learning' },
  { moduleId: 'advanced-rl-algorithms', courseId: 'reinforcement-learning' },
  { moduleId: 'owasp-testing', courseId: 'security-fundamentals' },
];

export interface InsertResult {
  success: string[];
  failed: Array<{ moduleId: string; error: string }>;
  skipped: Array<{ moduleId: string; reason: string }>;
}

/**
 * Insert all missing quizzes into the database
 */
export async function insertMissingQuizzes(dryRun: boolean = false): Promise<InsertResult> {
  const results: InsertResult = {
    success: [],
    failed: [],
    skipped: []
  };
  
  logger.info(`${dryRun ? '🔍 DRY RUN: ' : '📝 '}Inserting missing quizzes...`);
  
  for (const { moduleId, courseId } of MODULE_COURSE_MAPPINGS) {
    try {
      // Check if quiz already exists
      const { data: existing, error: fetchError } = await supabase
        .from('module_quizzes')
        .select('id')
        .eq('module_id', moduleId)
        .maybeSingle();
      
      if (fetchError) {
        logger.error(`Error checking ${moduleId}:`, fetchError);
        results.failed.push({ moduleId, error: fetchError.message });
        continue;
      }
      
      if (existing) {
        logger.info(`⊘ ${moduleId}: Already exists`);
        results.skipped.push({ moduleId, reason: 'Already exists' });
        continue;
      }
      
      // Generate quiz
      const generator = MISSING_MODULE_QUIZZES[moduleId];
      if (!generator) {
        logger.warn(`⚠️  ${moduleId}: No quiz generator found`);
        results.failed.push({ moduleId, error: 'No quiz generator found' });
        continue;
      }
      
      const quiz = generator();
      
      if (dryRun) {
        logger.info(`✓ ${moduleId}: Would insert ${quiz.questions.length} questions`);
        results.success.push(moduleId);
        continue;
      }
      
      // Insert into database (matching actual schema)
      const { error: insertError } = await supabase
        .from('module_quizzes')
        .insert({
          module_id: moduleId,
          course_id: courseId,
          quiz_title: `${moduleId} Quiz`,
          questions: quiz.questions,
          pass_threshold: quiz.passThreshold || 75,
          time_limit: quiz.timeLimit || 600,
          quiz_type: 'assessment',
          randomize_questions: true,
          show_feedback: true
        });
      
      if (insertError) {
        logger.error(`❌ ${moduleId}: Insert failed -`, insertError);
        results.failed.push({ moduleId, error: insertError.message });
        continue;
      }
      
      logger.info(`✅ ${moduleId}: Inserted ${quiz.questions.length} questions`);
      results.success.push(moduleId);
      
    } catch (error: any) {
      logger.error(`❌ ${moduleId}: Exception -`, error);
      results.failed.push({ moduleId, error: error.message });
    }
  }
  
  // Summary
  logger.info('\n' + '='.repeat(60));
  logger.info('📊 Insertion Summary');
  logger.info('='.repeat(60));
  logger.info(`Successful: ${results.success.length}`);
  logger.info(`Failed: ${results.failed.length}`);
  logger.info(`Skipped: ${results.skipped.length}`);
  logger.info(`Dry run: ${dryRun ? 'Yes' : 'No'}`);
  
  if (results.failed.length > 0) {
    logger.info('\nFailed modules:');
    results.failed.forEach(f => logger.error(`  - ${f.moduleId}: ${f.error}`));
  }
  
  return results;
}

// CLI execution (only runs in Node.js environment, not in browser)
if (typeof process !== 'undefined' && process.argv && import.meta.url === `file://${process.argv[1]}`) {
  const dryRun = process.argv.includes('--dry-run');
  
  insertMissingQuizzes(dryRun)
    .then(results => {
      const success = results.failed.length === 0;
      console.log(success ? '\n✅ Quiz insertion complete!' : '\n⚠️  Quiz insertion completed with errors');
      process.exit(success ? 0 : 1);
    })
    .catch(error => {
      console.error('\n❌ Quiz insertion failed:', error);
      process.exit(1);
    });
}
